/**
 * Runs Cost Summary Card
 * Displays estimated cost per run + monthly total.
 * Fetches costs in parallel for the supplied runs list.
 * Provides CSV export for internal billing.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Euro as EuroIcon,
} from '@mui/icons-material';
import { runsApi } from '../services/api';
import { Run, RunCostEstimate } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';

interface RunWithCost {
  run: Run;
  cost: RunCostEstimate | null;
  error: boolean;
}

interface RunsCostSummaryCardProps {
  runs: Run[];
}

export const RunsCostSummaryCard: React.FC<RunsCostSummaryCardProps> = ({ runs }) => {
  const [items, setItems] = useState<RunWithCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  // NC-1288 T31: the Monatssumme counts produktiv runs only, mirroring the API-side
  // statistics filter. Filtered inside the card rather than at the call site so the
  // sum, the rows and the CSV export can never describe different populations.
  // `classification` arrives already normalised from runs-list (persisted marker
  // first, legacy E2E- prefix as a read-time fallback) — the prefix rule is
  // deliberately NOT re-implemented here, same reasoning as RunClassificationChip.
  const produktivRuns = useMemo(() => runs.filter((r) => r.classification !== 'test'), [runs]);
  const excludedTestRuns = runs.length - produktivRuns.length;

  useEffect(() => {
    if (produktivRuns.length === 0) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all(
      produktivRuns.map(async (run): Promise<RunWithCost> => {
        // NC-921: sparse run docs (test artifacts / legacy runs) may lack a
        // runId. Skip the cost fetch so we never request /api/runs-cost/undefined
        // (404, auto-reported by the api.ts interceptor as a portal error). The
        // run still renders with a '—' cost cell.
        if (!run.runId) {
          return { run, cost: null, error: false };
        }
        try {
          const cost = await runsApi.getCost(run.runId);
          return { run, cost, error: false };
        } catch {
          return { run, cost: null, error: true };
        }
      })
    ).then((results) => {
      setItems(results);
      setFetchedAt(new Date());
      setLoading(false);
    });
  }, [produktivRuns]);

  const totalCost = items.reduce((sum, item) => sum + (item.cost?.totalCost ?? 0), 0);
  const currency = items.find((i) => i.cost)?.cost?.currency ?? 'EUR';

  const handleCsvExport = () => {
    // NC-867 T7: Kostenstelle column for chargeback grouping in the export.
    const header = ['RunID', 'Kostenstelle', 'Workload', 'VMs', 'VM-Größe', 'Stunden/Tag', 'Tage', 'Kosten/VM (EUR)', 'Gesamt (EUR)'];
    const rows = items
      .filter((i) => i.cost)
      .map((i) => [
        i.run.runId,
        i.cost!.costCenter ?? '',
        i.run.workloadId ?? i.run.workload ?? '',
        i.cost!.vmCount,
        i.cost!.vmSize,
        i.cost!.hoursPerDay,
        i.cost!.days,
        i.cost!.costPerVM.toFixed(2),
        i.cost!.totalCost.toFixed(2),
      ]);

    const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kosten-report-${formatDateSafe(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <EuroIcon color="primary" />
            <Typography variant="h6">Kosten-Übersicht</Typography>
          </Box>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleCsvExport}
            disabled={loading || items.filter((i) => i.cost).length === 0}
          >
            Als CSV exportieren
          </Button>
        </Box>

        {loading ? (
          <Box>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} data-testid="runs-cost-skeleton-line" height={40} sx={{ mb: 0.5 }} />
            ))}
          </Box>
        ) : items.length === 0 ? (
          <Alert severity="info">Keine Läufe verfügbar.</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Run-ID</TableCell>
                    <TableCell>Kostenstelle</TableCell>
                    <TableCell>Workload</TableCell>
                    <TableCell align="right">VMs</TableCell>
                    <TableCell align="right">Tage</TableCell>
                    <TableCell align="right">Geschätzte Kosten</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(({ run, cost, error }) => (
                    <TableRow key={run.runId} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{run.runId}</TableCell>
                      <TableCell>{cost?.costCenter ?? '—'}</TableCell>
                      <TableCell>{run.workloadId ?? run.workload ?? '—'}</TableCell>
                      <TableCell align="right">{cost?.vmCount ?? '—'}</TableCell>
                      <TableCell align="right">{cost?.days ?? '—'}</TableCell>
                      <TableCell align="right">
                        {error ? (
                          <Typography variant="body2" color="error">
                            Fehler
                          </Typography>
                        ) : cost ? (
                          <Typography variant="body2" fontWeight="medium">
                            {cost.totalCost.toFixed(2)} {cost.currency}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'primary.50',
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Geschätzter Gesamtaufwand ({items.filter((i) => i.cost).length} Läufe
                {excludedTestRuns > 0 && ', ohne Testläufe'})
              </Typography>
              <Typography variant="h6" color="primary.main" fontWeight="bold">
                {totalCost.toFixed(2)} {currency}
              </Typography>
            </Box>

            {fetchedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                * Schätzung basierend auf Azure Retail Prices. Letzte Aktualisierung:{' '}
                {formatDateSafe(fetchedAt, 'HH:mm:ss')}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
